import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchAccomplishmentSuggestions,
  fetchAccomplishments,
  acceptSuggestion,
  editAcceptSuggestion,
  dismissSuggestion,
} from '../../services/dashboardService';
import {
  Trophy,
  ArrowLeft,
  Sparkles,
  Check,
  X,
  Pencil,
  Loader2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Target,
  Zap,
} from 'lucide-react';

const CONFIDENCE_LABELS = {
  high: { min: 0.80, label: 'High confidence', color: 'text-green-400 bg-green-500/10' },
  medium: { min: 0.60, label: 'Medium confidence', color: 'text-amber-400 bg-amber-500/10' },
};

const getConfidenceInfo = (score) => {
  if (score >= 0.80) return CONFIDENCE_LABELS.high;
  return CONFIDENCE_LABELS.medium;
};

const AccomplishmentsPage = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [accomplishments, setAccomplishments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [tab, setTab] = useState('suggestions');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) loadData();
  }, [isAuthenticated]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [suggestionsRes, accomplishmentsRes] = await Promise.all([
        fetchAccomplishmentSuggestions(),
        fetchAccomplishments(),
      ]);
      setSuggestions(suggestionsRes.suggestions || []);
      setAccomplishments(accomplishmentsRes.accomplishments || []);
    } catch (err) {
      console.error('Failed to load accomplishments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (suggestionId) => {
    setActionLoading(suggestionId);
    try {
      await acceptSuggestion(suggestionId);
      await loadData();
    } catch (err) {
      console.error('Failed to accept:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismiss = async (suggestionId) => {
    setActionLoading(suggestionId);
    try {
      await dismissSuggestion(suggestionId);
      setSuggestions(prev => prev.filter(s => s.suggestion_id !== suggestionId));
    } catch (err) {
      console.error('Failed to dismiss:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleStartEdit = (suggestion) => {
    setEditingId(suggestion.suggestion_id);
    setEditForm({
      title: suggestion.suggested_title,
      context: suggestion.suggested_context,
      challenge: suggestion.suggested_challenge,
      outcome: suggestion.suggested_outcome,
      skills: suggestion.suggested_skills || [],
    });
  };

  const handleSaveEdit = async (suggestionId) => {
    setActionLoading(suggestionId);
    try {
      await editAcceptSuggestion(suggestionId, editForm);
      setEditingId(null);
      await loadData();
    } catch (err) {
      console.error('Failed to save edit:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="accomplishments-page">
      <header className="border-b border-zinc-800 sticky top-0 bg-zinc-950/95 backdrop-blur z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            <h1 className="text-lg font-semibold text-white">Accomplishments</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Tab switcher */}
        <div className="flex gap-1 mb-6 bg-zinc-900 rounded-lg p-1" data-testid="accomplishments-tabs">
          <button
            onClick={() => setTab('suggestions')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              tab === 'suggestions'
                ? 'bg-amber-500/20 text-amber-400'
                : 'text-zinc-400 hover:text-white'
            }`}
            data-testid="tab-suggestions"
          >
            <span className="flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4" />
              Suggestions
              {suggestions.length > 0 && (
                <span className="px-1.5 py-0.5 bg-amber-500/30 text-amber-300 rounded-full text-xs">
                  {suggestions.length}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setTab('confirmed')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              tab === 'confirmed'
                ? 'bg-green-500/20 text-green-400'
                : 'text-zinc-400 hover:text-white'
            }`}
            data-testid="tab-confirmed"
          >
            <span className="flex items-center justify-center gap-2">
              <Trophy className="w-4 h-4" />
              Confirmed
              {accomplishments.length > 0 && (
                <span className="px-1.5 py-0.5 bg-green-500/30 text-green-300 rounded-full text-xs">
                  {accomplishments.length}
                </span>
              )}
            </span>
          </button>
        </div>

        {/* Suggestions Tab */}
        {tab === 'suggestions' && (
          <div className="space-y-4" data-testid="suggestions-list">
            {suggestions.length === 0 ? (
              <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
                <Sparkles className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-400 mb-1">No pending suggestions</p>
                <p className="text-zinc-500 text-sm">
                  Keep reflecting with your Companion — we&apos;ll spot your achievements automatically
                </p>
              </div>
            ) : (
              <>
                <p className="text-zinc-400 text-sm mb-2">
                  We detected these potential accomplishments from your reflections. Review and confirm the ones that matter.
                </p>
                {suggestions.map(suggestion => (
                  <SuggestionCard
                    key={suggestion.suggestion_id}
                    suggestion={suggestion}
                    isEditing={editingId === suggestion.suggestion_id}
                    editForm={editForm}
                    setEditForm={setEditForm}
                    isLoading={actionLoading === suggestion.suggestion_id}
                    isExpanded={expandedId === suggestion.suggestion_id}
                    onToggleExpand={() => setExpandedId(
                      expandedId === suggestion.suggestion_id ? null : suggestion.suggestion_id
                    )}
                    onAccept={() => handleAccept(suggestion.suggestion_id)}
                    onDismiss={() => handleDismiss(suggestion.suggestion_id)}
                    onStartEdit={() => handleStartEdit(suggestion)}
                    onSaveEdit={() => handleSaveEdit(suggestion.suggestion_id)}
                    onCancelEdit={() => setEditingId(null)}
                  />
                ))}
              </>
            )}
          </div>
        )}

        {/* Confirmed Tab */}
        {tab === 'confirmed' && (
          <div className="space-y-4" data-testid="accomplishments-list">
            {accomplishments.length === 0 ? (
              <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
                <Trophy className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-400 mb-1">No confirmed accomplishments yet</p>
                <p className="text-zinc-500 text-sm">
                  Accept suggestions from the AI to build your accomplishment record
                </p>
              </div>
            ) : (
              accomplishments.map(acc => (
                <AccomplishmentCard key={acc.accomplishment_id} accomplishment={acc} />
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

const SuggestionCard = ({
  suggestion, isEditing, editForm, setEditForm, isLoading,
  isExpanded, onToggleExpand, onAccept, onDismiss, onStartEdit, onSaveEdit, onCancelEdit
}) => {
  const confidence = getConfidenceInfo(suggestion.confidence_score);

  if (isEditing) {
    return (
      <div className="bg-zinc-900 border border-amber-500/30 rounded-xl p-5" data-testid={`suggestion-edit-${suggestion.suggestion_id}`}>
        <div className="space-y-3">
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Title</label>
            <input
              type="text"
              value={editForm.title || ''}
              onChange={e => setEditForm({ ...editForm, title: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
              data-testid="edit-title-input"
            />
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Context</label>
            <textarea
              value={editForm.context || ''}
              onChange={e => setEditForm({ ...editForm, context: e.target.value })}
              rows={2}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none resize-none"
              data-testid="edit-context-input"
            />
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Challenge</label>
            <textarea
              value={editForm.challenge || ''}
              onChange={e => setEditForm({ ...editForm, challenge: e.target.value })}
              rows={2}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none resize-none"
              data-testid="edit-challenge-input"
            />
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Outcome</label>
            <textarea
              value={editForm.outcome || ''}
              onChange={e => setEditForm({ ...editForm, outcome: e.target.value })}
              rows={2}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none resize-none"
              data-testid="edit-outcome-input"
            />
          </div>
          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={onSaveEdit}
              disabled={isLoading}
              className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg text-sm transition-colors disabled:opacity-50"
              data-testid="save-edit-button"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save & Confirm
            </button>
            <button
              onClick={onCancelEdit}
              className="px-4 py-2 text-zinc-400 hover:text-white text-sm transition-colors"
              data-testid="cancel-edit-button"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-700 transition-colors" data-testid={`suggestion-card-${suggestion.suggestion_id}`}>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-medium leading-snug">{suggestion.suggested_title}</h3>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${confidence.color}`}>
            {Math.round(suggestion.confidence_score * 100)}%
          </span>
        </div>

        {suggestion.suggested_skills?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {suggestion.suggested_skills.map((skill, i) => (
              <span key={i} className="px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded text-xs">
                {skill}
              </span>
            ))}
          </div>
        )}

        {/* Expandable details */}
        <button
          onClick={onToggleExpand}
          className="flex items-center gap-1 text-zinc-500 hover:text-zinc-300 text-xs transition-colors mb-3"
          data-testid="toggle-details-button"
        >
          {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {isExpanded ? 'Hide details' : 'Show details'}
        </button>

        {isExpanded && (
          <div className="space-y-2 mb-3 text-sm">
            {suggestion.suggested_context && (
              <div>
                <span className="text-zinc-500 text-xs">Context:</span>
                <p className="text-zinc-300">{suggestion.suggested_context}</p>
              </div>
            )}
            {suggestion.suggested_challenge && (
              <div>
                <span className="text-zinc-500 text-xs">Challenge:</span>
                <p className="text-zinc-300">{suggestion.suggested_challenge}</p>
              </div>
            )}
            {suggestion.suggested_outcome && (
              <div>
                <span className="text-zinc-500 text-xs">Outcome:</span>
                <p className="text-zinc-300">{suggestion.suggested_outcome}</p>
              </div>
            )}
            {suggestion.detection_signals?.length > 0 && (
              <div className="flex items-center gap-1 text-zinc-600 text-xs pt-1">
                <AlertCircle className="w-3 h-3" />
                Detected from: {suggestion.detection_signals.slice(0, 3).map(s => s.split(':')[1]).join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={onAccept}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/20 rounded-lg text-sm transition-colors disabled:opacity-50"
            data-testid="accept-button"
          >
            {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Accept
          </button>
          <button
            onClick={onStartEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm transition-colors"
            data-testid="edit-button"
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </button>
          <button
            onClick={onDismiss}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-zinc-500 hover:text-zinc-300 text-sm transition-colors disabled:opacity-50"
            data-testid="dismiss-button"
          >
            <X className="w-3.5 h-3.5" />
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

const AccomplishmentCard = ({ accomplishment }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid={`accomplishment-card-${accomplishment.accomplishment_id}`}>
    <div className="flex items-start gap-3 mb-3">
      <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
        <Trophy className="w-4 h-4 text-green-400" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-white font-medium leading-snug">{accomplishment.title}</h3>
        {accomplishment.timeframe?.start && (
          <p className="text-zinc-500 text-xs mt-1">
            {new Date(accomplishment.timeframe.start).toLocaleDateString()}
            {accomplishment.timeframe.end && accomplishment.timeframe.end !== accomplishment.timeframe.start && (
              <> — {new Date(accomplishment.timeframe.end).toLocaleDateString()}</>
            )}
          </p>
        )}
      </div>
    </div>

    {accomplishment.context && (
      <p className="text-zinc-400 text-sm mb-3">{accomplishment.context}</p>
    )}

    {accomplishment.outcome && (
      <div className="flex items-start gap-2 mb-3">
        <Target className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
        <p className="text-zinc-300 text-sm">{accomplishment.outcome}</p>
      </div>
    )}

    {accomplishment.skills?.length > 0 && (
      <div className="flex flex-wrap gap-1.5">
        {accomplishment.skills.map((skill, i) => (
          <span key={i} className="px-2 py-0.5 bg-green-500/10 text-green-400/80 rounded text-xs">
            {skill}
          </span>
        ))}
      </div>
    )}

    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800">
      <Zap className="w-3 h-3 text-zinc-600" />
      <span className="text-zinc-600 text-xs">
        {accomplishment.source_type === 'ai_suggested' ? 'AI detected' : 'Manually added'}
        {accomplishment.original_confidence_score && ` (${Math.round(accomplishment.original_confidence_score * 100)}% confidence)`}
      </span>
    </div>
  </div>
);

export default AccomplishmentsPage;
